import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  TrendingUp, 
  BarChart3, 
  Network, 
  Sparkles, 
  ChevronRight,
  Zap,
  Target,
  Activity,
  Download,
  RefreshCw
} from "lucide-react";
import UploadSection from "../components/assessment/UploadSection";
import OverallAssessment from "../components/assessment/OverallAssessment";
import MaturityAssessment from "../components/assessment/MaturityAssessment";
import ProcessDeepDive from "../components/assessment/ProcessDeepDive";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Progress } from "../components/ui/progress";
import { useToast } from "../hooks/use-toast";
import { API_BASE_URL } from "../lib/api";
import './index.css'

const Index = () => {
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [activeTab, setActiveTab] = useState("overall");
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationProgress, setRegenerationProgress] = useState(0);
  const [regenerationMessage, setRegenerationMessage] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    const uniqueId = assessmentResult?.dashboard_data?.unique_id;
    if (uniqueId) {
      localStorage.setItem("dashboard_unique_id", uniqueId);
    }
  }, [assessmentResult]);

  const hasResult = Boolean(assessmentResult);
  const uniqueId = assessmentResult?.dashboard_data?.unique_id;
  const downloadUrl = assessmentResult?.download_url;

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
    }
  };

  const pollRegenerationStatus = async (jobId) => {
    const maxAttempts = 400;
    let attempts = 0;
    const poll = async () => {
      const response = await fetch(`${API_BASE_URL}/valueassessment/status/${jobId}`, { method: "GET" });
      if (!response.ok) throw new Error("Failed to check regeneration status");
      const statusData = await response.json();
      setRegenerationProgress(statusData.progress || 0);
      setRegenerationMessage(statusData.message || "Processing...");
      if (statusData.status === "completed") return statusData.result;
      if (statusData.status === "failed") throw new Error(statusData.error || "Regeneration failed");
      attempts++;
      if (attempts >= maxAttempts) throw new Error("Regeneration timeout. Please try again.");
      await new Promise((resolve) => setTimeout(resolve, 3000));
      return await poll();
    };
    return await poll();
  };

  const handleRegenerate = async () => {
    if (!uniqueId) {
      toast({
        title: "Missing ID",
        description: "Unique ID is required to regenerate the dashboard.",
        variant: "destructive",
      });
      return;
    }

    setIsRegenerating(true);
    setRegenerationProgress(0);
    setRegenerationMessage("Starting regeneration...");

    try {
      const response = await fetch(`${API_BASE_URL}/regenerate_report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unique_id: uniqueId,
          user_prompt: "Regenerate report with updated guardrails and maturity principles",
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.error ?? "Failed to start regeneration.");
      }

      const startPayload = await response.json();
      const jobId = startPayload.job_id;

      toast({
        title: "Regeneration Started",
        description: "This may take several minutes.",
      });

      const result = await pollRegenerationStatus(jobId);
      setAssessmentResult(result);
      toast({ title: "Dashboard Updated", description: "Report regenerated successfully." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to regenerate report.";
      toast({ title: "Regeneration Failed", description: message, variant: "destructive" });
    } finally {
      setIsRegenerating(false);
      setShowRegenerateDialog(false);
      setRegenerationProgress(0);
      setRegenerationMessage("");
    }
  };

  return (
    <div className="va-root">
      {/* Animated background elements */}
      <div className="va-bg-orbs">
        <div className="va-orb va-orb-1"></div>
        <div className="va-orb va-orb-2"></div>
        <div className="va-orb va-orb-3"></div>
      </div>

      {/* Header */}
      {!hasResult && (
        <header className="va-header">
          <div className="va-header-glow"></div>
          <div className="va-header-inner">
            <div className="va-header-text">
              <div className="va-pill">
                <span className="va-pill-icon">
                  <Sparkles className="va-pill-icon-svg" />
                </span>
                <span>AI-POWERED INSIGHTS</span>
                <span className="va-pill-pulse"></span>
              </div>
              <h1 className="va-title">
                AI Value Assessment Platform
                <span className="va-title-gradient-text"> for Procurement</span>
              </h1>
              <p className="va-subtitle">
                Unlock comprehensive intelligence across your procurement landscape. Our AI-driven platform evaluates opportunities, measures maturity, and identifies transformational process improvements in minutes.
              </p>

              {/* Feature highlights */}
              <div className="va-header-features">
                <div className="va-feature-item">
                  <Zap className="va-feature-icon" />
                  <span>Real-time Analysis</span>
                </div>
                <div className="va-feature-item">
                  <Target className="va-feature-icon" />
                  <span>Actionable Insights</span>
                </div>
                <div className="va-feature-item">
                  <Activity className="va-feature-icon" />
                  <span>Live Monitoring</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main */}
      <main className="va-main">
        {!hasResult ? (
          <div className="va-main-inner">
            {/* Intro card with enhanced visual hierarchy */}
            <section className="va-card va-card-intro">
              <div className="va-card-glow"></div>
              <div className="va-card-intro-layout">
                <div className="va-card-intro-icon-wrap">
                  <TrendingUp className="va-card-intro-icon" />
                  <div className="va-card-intro-icon-bg"></div>
                </div>
                <div className="va-card-intro-body">
                  <div className="va-card-intro-header">
                    <h2 className="va-card-intro-title">
                      Launch Your Assessment Journey
                    </h2>
                    <ChevronRight className="va-card-intro-arrow" />
                  </div>
                  <p className="va-card-intro-text">
                    Transform raw procurement data into strategic intelligence. Our advanced AI engine automatically evaluates value pools, assesses organizational maturity, and pinpoints high-impact process optimization opportunities—delivering a comprehensive, reusable dashboard experience.
                  </p>
                  <div className="va-card-intro-divider"></div>
                  <div className="va-card-intro-tags">
                    <div className="va-tag">
                      <div className="va-tag-icon">
                        <TrendingUp size={14} />
                      </div>
                      <span>AI Value & ROI Mapping</span>
                    </div>
                    <div className="va-tag">
                      <div className="va-tag-icon">
                        <BarChart3 size={14} />
                      </div>
                      <span>Maturity Assessment</span>
                    </div>
                    <div className="va-tag">
                      <div className="va-tag-icon">
                        <Network size={14} />
                      </div>
                      <span>Process Intelligence</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Upload card with modern styling */}
            <section className="va-card va-card-upload">
              <div className="va-upload-decoration">
                <div className="va-upload-decoration-line"></div>
                <div className="va-upload-decoration-dot"></div>
              </div>
              <UploadSection onUploadComplete={setAssessmentResult} />
            </section>
          </div>
        ) : (
          <div className="va-main-inner">
            <section className="va-card va-card-tabs">
              <div className="va-card-glow"></div>
              
              <div className="va-tabs-header">
                <div className="va-tabs-header-content">
                  <div className="va-tabs-label-wrapper">
                    <Activity className="va-tabs-label-icon" />
                    <p className="va-tabs-label">Interactive Dashboard</p>
                  </div>
                  <h3 className="va-tabs-title">Assessment Overview</h3>
                  <p className="va-tabs-description">
                    Navigate between strategic value analysis, organizational maturity metrics, and granular process-level insights.
                  </p>
                </div>
                <div className="va-tabs-actions">
                  <div className="va-tabs-legend">
                    <div className={`va-tabs-legend-pill ${activeTab === "overall" ? "va-tabs-legend-pill--primary" : ""}`}>
                      <span className="va-tabs-legend-icon-wrap">
                        <TrendingUp className="va-tabs-legend-icon" />
                      </span>
                      <span>Overall Value</span>
                      <span className="va-legend-badge">3</span>
                    </div>
                    <div className={`va-tabs-legend-pill ${activeTab === "maturity" ? "va-tabs-legend-pill--primary" : ""}`}>
                      <span className="va-tabs-legend-icon-wrap">
                        <BarChart3 className="va-tabs-legend-icon" />
                      </span>
                      <span>Maturity Lens</span>
                      <span className="va-legend-badge">5</span>
                    </div>
                    <div className={`va-tabs-legend-pill ${activeTab === "process" ? "va-tabs-legend-pill--primary" : ""}`}>
                      <span className="va-tabs-legend-icon-wrap">
                        <Network className="va-tabs-legend-icon" />
                      </span>
                      <span>Process Deep Dive</span>
                      <span className="va-legend-badge">8</span>
                    </div>
                  </div>
                  <div className="va-action-buttons">
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowRegenerateDialog(true)} disabled={!uniqueId || isRegenerating}>
                      <RefreshCw className="h-4 w-4" />
                      {isRegenerating ? "Regenerating..." : "Regenerate"}
                    </Button>
                    <Button variant="outline" size="sm" className="gap-2" onClick={handleDownload} disabled={!downloadUrl}>
                      <Download className="h-4 w-4" />
                      Download Report
                    </Button>
                  </div>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="va-tabs">
                <div className="va-tabs-list-wrapper">
                  <TabsList className="va-tabs-list">
                    <TabsTrigger value="overall" className="va-tab-trigger">
                      <div className="va-tab-trigger-content">
                        <TrendingUp className="va-tab-trigger-icon" />
                        <span className="va-tab-trigger-text">Overall Assessment</span>
                      </div>
                      <div className="va-tab-trigger-shine"></div>
                    </TabsTrigger>
                    <TabsTrigger value="maturity" className="va-tab-trigger">
                      <div className="va-tab-trigger-content">
                        <BarChart3 className="va-tab-trigger-icon" />
                        <span className="va-tab-trigger-text">Maturity & Readiness</span>
                      </div>
                      <div className="va-tab-trigger-shine"></div>
                    </TabsTrigger>
                    <TabsTrigger value="process" className="va-tab-trigger">
                      <div className="va-tab-trigger-content">
                        <Network className="va-tab-trigger-icon" />
                        <span className="va-tab-trigger-text">Process Deep Dive</span>
                      </div>
                      <div className="va-tab-trigger-shine"></div>
                    </TabsTrigger>
                  </TabsList>
                  <div className="va-tabs-indicator"></div>
                </div>

                <TabsContent value="overall" className="va-tab-content">
                  <OverallAssessment
                    data={assessmentResult.dashboard_data}
                  />
                </TabsContent>

                <TabsContent value="maturity" className="va-tab-content">
                  <MaturityAssessment data={assessmentResult.dashboard_data} />
                </TabsContent>

                <TabsContent value="process" className="va-tab-content">
                  <ProcessDeepDive data={assessmentResult.dashboard_data} />
                </TabsContent>
              </Tabs>
            </section>
          </div>
        )}
      </main>

      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent className="regenerate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate dashboard?</AlertDialogTitle>
            <AlertDialogDescription>
              This will re-run the assessment using the latest edited guardrails and structured data.
              The process can take several minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-between text-sm font-semibold">
              <span className="text-muted-foreground">{regenerationProgress}%</span>
              <Progress value={regenerationProgress} className="h-2" />
            </div>
            <p className="text-sm text-muted-foreground">{regenerationMessage}</p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRegenerating}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate} disabled={isRegenerating}>
              {isRegenerating ? `Regenerating... (${regenerationProgress}%)` : "Continue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating elements for depth */}
      <div className="va-floating-elements">
        <div className="va-floating-dot va-floating-dot-1"></div>
        <div className="va-floating-dot va-floating-dot-2"></div>
        <div className="va-floating-dot va-floating-dot-3"></div>
      </div>
    </div>
  );
};

export default Index;
